/**
 * Kochmesser und Schneidebrett in der Draufsicht.
 *
 * Das Messer ist als klassisches Kochmesser gebaut: lange Klinge mit gerader
 * Schneide, die zur Spitze hin aufsteigt, Klingenrücken, Kropf und ein dunkler
 * Griff mit drei Nieten. Im eigenen Koordinatensystem liegt die Schneide auf
 * der x-Achse, die Spitze zeigt nach links, der Übergang zum Griff liegt im
 * Ursprung. Gedreht um 90 Grad zeigt die Spitze nach oben.
 *
 * Das Anheben wird über den Schatten gezeigt: Je höher das Messer, desto
 * weiter liegt der Schatten versetzt und desto größer wirkt das Messer.
 */
import { STROKE } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { circle, group, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';

/** Klingenlänge im eigenen Koordinatensystem. */
export const KNIFE_BLADE = 88;

/** Umriss der Klinge. */
const BLADE = path()
  .move(0, 0)
  .line(-56, 0)
  .curve(-70, 0, -81, -1.5, -88, -5.5)
  .curve(-77, -12, -62, -18.5, -46, -19)
  .line(0, -19)
  .close()
  .toString();

/** Umriss des Griffs. */
const HANDLE = roundedRectPath(7, -15.5, 46, 13, 6);

/** Schattenform: Klinge und Griff zusammen. */
const SILHOUETTE = `${BLADE}${HANDLE}`;

/** Haltung des Messers. */
export interface KnifePose {
  /** Lage des Klingenansatzes an der Schneide. */
  readonly x: number;
  readonly y: number;
  /** Drehung in Grad; 0 = Spitze links, 90 = Spitze oben. */
  readonly rotate: number;
  /** Anheben von 0 (liegt auf) bis 1 (angehoben). */
  readonly lift: number;
  readonly scale?: number;
  /**
   * Sichtbare Breite der Klinge, 1 = flach liegend. Beim Schneiden steht die
   * Klinge aufrecht; von oben sieht man sie dann schmaler und die Zutat
   * darunter bleibt sichtbar.
   */
  readonly squash?: number;
}

/** Messer ohne Schatten im eigenen Koordinatensystem. */
function knifeBody(): SceneNode[] {
  return [
    shape(BLADE, styles.solid('paper')),
    // Schliff entlang der Schneide
    shape(
      path().move(-4, -4.5).line(-55, -4.5).curve(-66, -4.5, -76, -5.5, -83, -7.5).toString(),
      styles.fine('shade'),
    ),
    // Kropf
    rect(0, -20.5, 7, 22, 2, styles.solid('deep')),
    // Griff mit Nieten
    shape(HANDLE, { fill: 'ink', stroke: 'ink', strokeWidth: STROKE.outline }),
    circle(19, -9, 1.7, styles.flat('paper')),
    circle(31, -9, 1.7, styles.flat('paper')),
    circle(43, -9, 1.7, styles.flat('paper')),
  ];
}

/** Messer mit Schatten in der Draufsicht. */
export function knife(pose: KnifePose): SceneNode {
  const scale = (pose.scale ?? 1) * (1 + 0.07 * pose.lift);
  const shadowScale = pose.scale ?? 1;
  const squash = pose.squash ?? 1;
  const dx = 3 + 9 * pose.lift;
  const dy = 4 + 11 * pose.lift;
  return group([
    group([shape(SILHOUETTE, styles.flat('shade'))], {
      transform: {
        x: pose.x + dx,
        y: pose.y + dy,
        rotate: pose.rotate,
        scaleX: shadowScale,
        scaleY: shadowScale * squash,
      },
    }),
    group(knifeBody(), {
      transform: { x: pose.x, y: pose.y, rotate: pose.rotate, scaleX: scale, scaleY: scale * squash },
    }),
  ]);
}

/** Grenzen des Bretts in der Draufsicht. */
export const BOARD = { x: 12, y: 14, width: 216, height: 124 } as const;

/** Schneidebrett in der Draufsicht mit Kante, Maserung und Griffloch. */
export function board(): SceneNode[] {
  const { x, y, width, height } = BOARD;
  const grain: SceneNode[] = [];
  const lines = [
    [0.18, 0.1, 0.55],
    [0.34, 0.42, 0.92],
    [0.52, 0.06, 0.4],
    [0.67, 0.5, 0.86],
    [0.84, 0.16, 0.62],
  ] as const;
  for (const [row, from, to] of lines) {
    const gy = y + height * row;
    grain.push(
      shape(
        path()
          .move(x + width * from, gy)
          .curve(x + width * (from + 0.1), gy - 2, x + width * (to - 0.1), gy + 2, x + width * to, gy)
          .toString(),
        styles.fine('shade'),
      ),
    );
  }
  return [
    // Kante des Bretts als schmales Band darunter
    shape(roundedRectPath(x, y + 6, width, height, 14), styles.solid('shade')),
    shape(roundedRectPath(x, y, width, height, 14), styles.solid('tint')),
    ...grain,
    // Griffloch
    circle(x + width - 16, y + 16, 5, { fill: 'backdrop', stroke: 'ink', strokeWidth: STROKE.detail }),
    line(x + 10, y + height - 7, x + width - 10, y + height - 7, styles.fine('shade')),
  ];
}
